import React, { useEffect, useState } from 'react';
import { X, Download, Calendar, TrendingUp, Building2, FileText } from 'lucide-react';
import { API_BASE } from '../../constants';

function CompanyReportModal({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [reportType, setReportType] = useState('ytd'); // 'ytd' or 'annual'
  const [historicalReports, setHistoricalReports] = useState([]);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  useEffect(() => {
    fetchReportData();
    fetchHistoricalReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, selectedYear]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const endpoint = reportType === 'ytd' 
        ? `${API_BASE}/admin/reports/companies/ytd`
        : `${API_BASE}/admin/reports/companies/annual/${selectedYear}`;
      
      const res = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) {
        throw new Error('Failed to fetch report data');
      }
      
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      console.error('Failed to fetch report:', err);
      alert('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoricalReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/admin/reports/companies/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setHistoricalReports(data.reports || []);
      }
    } catch (err) {
      console.error('Failed to fetch historical reports:', err);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setGeneratingPDF(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (reportType === 'annual') {
        params.append('year', selectedYear);
      }
      params.append('report_type', reportType);
      
      const res = await fetch(`${API_BASE}/admin/reports/companies/generate-pdf?${params}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      // Download the PDF
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = reportType === 'ytd' 
        ? `Company_Report_${selectedYear}_YTD.pdf`
        : `Company_Report_${selectedYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // Refresh historical reports list
      fetchHistoricalReports();
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      alert('Failed to generate PDF report');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleDownloadHistoricalReport = async (year) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/admin/reports/companies/download/${year}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) {
        throw new Error('Failed to download report');
      }
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Company_Report_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download historical report:', err);
      alert('Failed to download report');
    }
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short' });
  };

  const getMonthColumns = () => {
    if (!reportData || !reportData.monthly_breakdown || reportData.monthly_breakdown.length === 0) {
      return [];
    }
    
    // Get all unique months from the first company
    const firstCompany = reportData.monthly_breakdown[0];
    return Object.keys(firstCompany.monthly_data || {}).sort();
  };

  const getAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Show last 5 years
    for (let i = 0; i < 5; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  const monthColumns = getMonthColumns();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-green-50 to-green-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-600 rounded-lg">
              <TrendingUp className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Company Submission Report</h2>
              <p className="text-sm text-gray-600 mt-1">
                {reportType === 'ytd' 
                  ? `Year-to-Date: January 1, ${selectedYear} - ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                  : `Annual Report: January 1, ${selectedYear} - December 31, ${selectedYear}`
                }
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-2 hover:bg-white rounded-full transition">
            <X size={24} />
          </button>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between p-4 bg-gray-50 border-b flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2">
              <Calendar size={18} className="text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Report Type:</span>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="ytd">Year-to-Date</option>
                <option value="annual">Annual</option>
              </select>
            </label>
            
            {reportType === 'annual' && (
              <label className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Year:</span>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {getAvailableYears().map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <button
            onClick={handleDownloadPDF}
            disabled={generatingPDF}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {generatingPDF ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Generating...
              </>
            ) : (
              <>
                <Download size={18} />
                Download PDF
              </>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {reportData && (
            <div className="space-y-8">
              {/* Summary Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 size={20} className="text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">Total Companies</span>
                  </div>
                  <div className="text-3xl font-bold text-blue-900">
                    {reportData.summary.length}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText size={20} className="text-green-600" />
                    <span className="text-sm font-medium text-green-900">Total Samples</span>
                  </div>
                  <div className="text-3xl font-bold text-green-900">
                    {reportData.grand_total}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp size={20} className="text-purple-600" />
                    <span className="text-sm font-medium text-purple-900">Average per Company</span>
                  </div>
                  <div className="text-3xl font-bold text-purple-900">
                    {Math.round(reportData.grand_total / reportData.summary.length)}
                  </div>
                </div>
              </div>

              {/* Summary Table */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Summary</h3>
                <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-green-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Company Name
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                          Total Samples
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                          % of Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reportData.summary.map((company, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {company.company_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center font-semibold">
                            {company.total_batches}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {company.percentage}%
                            </span>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-green-50 border-t-2 border-green-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                          TOTAL
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-center">
                          {reportData.grand_total}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-center">
                          100.0%
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Monthly Breakdown */}
              {monthColumns.length > 0 && (
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Monthly Breakdown</h3>
                  <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-green-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider sticky left-0 bg-green-700 z-10">
                            Company
                          </th>
                          {monthColumns.map(month => (
                            <th key={month} className="px-3 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                              {formatMonth(month)}
                            </th>
                          ))}
                          <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider bg-green-800">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {reportData.monthly_breakdown.map((company, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-inherit">
                              {company.company_name}
                            </td>
                            {monthColumns.map(month => (
                              <td key={month} className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                                {company.monthly_data[month] || '-'}
                              </td>
                            ))}
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-center bg-gray-100">
                              {company.total}
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-green-50 border-t-2 border-green-700">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 sticky left-0 bg-green-50">
                            Monthly Total
                          </td>
                          {monthColumns.map(month => (
                            <td key={month} className="px-3 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-center">
                              {reportData.monthly_totals[month] || '-'}
                            </td>
                          ))}
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-center bg-green-100">
                            {reportData.grand_total}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Historical Reports */}
              {historicalReports.length > 0 && (
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Historical Reports</h3>
                  <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {historicalReports.map((report, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleDownloadHistoricalReport(report.year)}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-green-500 transition group"
                        >
                          <div className="flex items-center gap-3">
                            <FileText size={20} className="text-gray-400 group-hover:text-green-600" />
                            <div className="text-left">
                              <div className="font-medium text-gray-900">{report.year}</div>
                              <div className="text-xs text-gray-500">
                                {new Date(report.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <Download size={18} className="text-gray-400 group-hover:text-green-600" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 text-center">
          <p className="text-xs text-gray-500">
            Report generated on {new Date().toLocaleDateString('en-US', { 
              month: 'long', 
              day: 'numeric', 
              year: 'numeric',
              hour: 'numeric',
              minute: 'numeric'
            })}
          </p>
        </div>
      </div>
    </div>
  );
}

export default CompanyReportModal;
